import { useState, useCallback, useEffect } from 'react';

export interface UserState {
  username: string;
  email: string;
  sessionId: string;
  isSettingUsername: boolean;
  hasStartedChatting: boolean;
}

export interface UserActions {
  setUsername: (username: string) => void;
  setEmail: (email: string) => void;
  setSessionId: (sessionId: string) => void;
  setIsSettingUsername: (setting: boolean) => void;
  setHasStartedChatting: (started: boolean) => void;
  handleSetUser: (username: string, email: string) => void;
  clearUserData: () => void;
}

// Custom hook for localStorage operations
function useLocalStorageState<T>(
  key: string,
  defaultValue: T,
  transform?: (value: string) => T
): [T, (value: T) => void] {
  const [state, setState] = useState<T>(() => {
    if (typeof window === 'undefined') return defaultValue;

    try {
      const saved = localStorage.getItem(key);
      if (saved === null) return defaultValue;

      return transform ? transform(saved) : (saved as unknown as T);
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  const setValue = useCallback(
    (value: T) => {
      setState(value);

      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem(key, String(value));
        }
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key]
  );

  return [state, setValue];
}

export function useUserState(): UserState & UserActions {
  const [username, setUsernameLocal] = useLocalStorageState('chat-username', '');
  const [email, setEmailLocal] = useLocalStorageState('chat-email', '');
  const [sessionId, setSessionIdLocal] = useLocalStorageState('chat-session-id', '');
  const [hasStartedChatting, setHasStartedChattingLocal] = useLocalStorageState(
    'has-started-chatting',
    false,
    (value) => value === 'true'
  );

  const [isSettingUsername, setIsSettingUsername] = useState(() => !username || !email);

  // Update isSettingUsername when username or email changes
  useEffect(() => {
    setIsSettingUsername(!username || !email);
  }, [username, email]);

  const setUsername = useCallback(
    (newUsername: string) => {
      setUsernameLocal(newUsername);
    },
    [setUsernameLocal]
  );

  const setEmail = useCallback(
    (newEmail: string) => {
      setEmailLocal(newEmail);
    },
    [setEmailLocal]
  );

  const setSessionId = useCallback(
    (newSessionId: string) => {
      setSessionIdLocal(newSessionId);
    },
    [setSessionIdLocal]
  );

  const setHasStartedChatting = useCallback(
    (started: boolean) => {
      setHasStartedChattingLocal(started);
    },
    [setHasStartedChattingLocal]
  );

  const handleSetUser = useCallback(
    (newUsername: string, newEmail: string) => {
      if (newUsername.trim() && newEmail.trim()) {
        setUsername(newUsername.trim());
        setEmail(newEmail.trim());
        setIsSettingUsername(false);
        setHasStartedChatting(true);

        // Clear old messages for fresh start
        if (typeof window !== 'undefined') {
          localStorage.setItem('chat-messages', '[]');
        }
      }
    },
    [setUsername, setEmail, setHasStartedChatting]
  );

  const clearUserData = useCallback(() => {
    setUsernameLocal('');
    setEmailLocal('');
    setSessionIdLocal(''); // Clear session on logout
    setHasStartedChattingLocal(false);
    setIsSettingUsername(true);

    // Clear messages
    if (typeof window !== 'undefined') {
      localStorage.removeItem('chat-messages');
      localStorage.setItem('chat-messages', '[]');
    }
  }, [setUsernameLocal, setEmailLocal, setSessionIdLocal, setHasStartedChattingLocal]);

  return {
    // State
    username,
    email,
    sessionId,
    isSettingUsername,
    hasStartedChatting,

    // Actions
    setUsername,
    setEmail,
    setSessionId,
    setIsSettingUsername,
    setHasStartedChatting,
    handleSetUser,
    clearUserData,
  };
}
